import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  Signal,
  computed,
  inject,
} from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';

import {
  ButtonComponent,
  IGenCatForm,
  NgGenCatFormComponent,
} from '@ctti-dev/ng-gencat';

import { ButtonDisableDirective } from '@shared/directives/button-disable.directive';
import { AuthService } from '@core/services/auth.service';
import { LOGIN_FORM_CONFIG } from './configurations/login-config';
import { TranslatePipe } from '@ngx-translate/core';
import { CognitoUserPools } from '@core/models/cognito.model';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthContextService } from '@core/services/auth-context.service';
import { PRIMARY_BUTTON } from '@shared/utils/utils.config';
import { ButtonConfigDirective } from '@shared/directives/button-config.directive';

type LoginMode = 'default' | 'upload-files';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    FormsModule,
    ButtonComponent,
    NgGenCatFormComponent,
    ButtonDisableDirective,
    TranslatePipe,
    ButtonConfigDirective,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LoginComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _authCtx = inject(AuthContextService);

  selectedProvider: string = '';
  loginPoolsForm!: UntypedFormGroup;

  readonly primaryButton = PRIMARY_BUTTON;

  mode: LoginMode = 'default';

  readonly readyLoginSignal: Signal<boolean> = computed(
    () => !!this._authService.selectedProviderConfig(),
  );

  readonly loginFormConfigSignal: Signal<IGenCatForm> = computed(() => {
    const baseForm = LOGIN_FORM_CONFIG;
    const options = this._authService.userPools().map((pool) => ({
      label: pool.agencyName,
      value: pool.userPoolId,
    }));
    return { ...baseForm, pools: { ...baseForm['pools'], options } };
  });

  readonly showForgotPasswordLinkSignal: Signal<boolean> = computed(() => {
    return Boolean(
      this._authService
        .userPools()
        .find(
          (provider: CognitoUserPools) =>
            this._authService.selectedProvider() === provider.userPoolId &&
            provider.idps.length === 0,
        ),
    );
  });

  async ngOnInit(): Promise<void> {
    this.mode = (this._route.snapshot.data['loginMode'] ??
      'default') as LoginMode;

    localStorage.setItem('appLoginMode', this.mode);

    this._authService.setActivePoolsMode(this.mode);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (this._authService as any).waitStorageReady?.();

    const session = await this._authService.safeFetchAuthSession();
    const logged = !!session?.tokens?.idToken;

    if (logged) {
      this._authService.setAuthenticated(true);
      const target = this.mode === 'upload-files' ? '/upload' : '/home';
      this._router.navigateByUrl(target);
      return;
    }

    await this._authService.ensureUserPoolsLoaded(this.mode);

    const saved: string | null = this._authService.selectedProvider();
    if (saved) this.selectedProvider = saved;
  }

  async login(): Promise<void> {
    if (!this.readyLoginSignal()) return;

    this._authCtx.setMode(this.mode);

    const target = this.mode === 'upload-files' ? '/upload' : '/home';
    this._authCtx.setPostLoginTarget(target);

    const cfg = this._authService.selectedProviderConfig();
    await this._authService.login(cfg?.idp);
  }

  loginPoolsFormLoaded(form: UntypedFormGroup): void {
    this.loginPoolsForm = form;
    this.loginPoolsForm.valueChanges.subscribe(() => {
      const newValue = this.loginPoolsForm.value?.pools;
      if (typeof newValue === 'string') {
        this.selectedProvider = newValue;
        this._authService.saveSelectedProvider(this.selectedProvider);
      }
    });
  }

  redirectToForgotPassword(): void {
    this._router.navigateByUrl('forgot-password');
  }
}
